import { FC, ReactNode, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { FieldProps, useField } from 'formik';
import { Autocomplete, FormControl, FormHelperText, TextField } from '@mui/material';
import { WixContactsLabel } from '../../types';

type Option = { id: string; label: string };

type AutocompeteInputFieldFieldProps = FieldProps & {
  submitOnBlur?: boolean;
  onBlur?: () => void;
  options: Option[];
  label?: string;
  helperText?: ReactNode;
  multiple?: boolean;
  disableInput?: boolean;
};

export const AutocompeteInputField: FC<AutocompeteInputFieldFieldProps> = ({
  field,
  form,
  submitOnBlur = true,
  options,
  onBlur,
  label,
  multiple = false,
  ...restProps
}) => {
  const [, meta, helpers] = useField(field.name);

  const helperText = meta.error || restProps.helperText;

  const handleChange = useCallback(
    (event, value) => {
      field.onChange(event);

      if (!value || value.length === 0) {
        helpers.setValue(null);
        form.submitForm();
        return;
      }

      if (multiple) {
        helpers.setValue(value.map((v: WixContactsLabel) => v.id));
      } else {
        helpers.setValue(value.id);
      }

      form.submitForm();
    },
    [field, helpers, multiple, form]
  );

  const renderInput = useCallback(
    (opts) => {
      return (
        <TextField
          {...opts}
          label={label}
          inputProps={{
            ...opts.inputProps,
            autoComplete: 'off',
          }}
          helperText={helperText}
        />
      );
    },
    [helperText, label]
  );

  const defaultValue = useMemo(() => {
    if (multiple) {
      return options.filter((option) => field.value?.includes(option.id));
    }

    return options.find((option) => option.id === field.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormControl sx={{ marginTop: '12px' }}>
      <StyledAutocomplete
        size="small"
        options={options}
        defaultValue={defaultValue}
        renderInput={renderInput}
        onChange={handleChange}
        multiple={multiple}
        clearIcon={false}
        {...restProps}
      />

      {helperText && <FormHelperText error={Boolean(meta.error)}>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export const AutocompeteLoadingInputField: FC<AutocompeteInputFieldFieldProps> = ({ label }) => {
  const renderInput = useCallback(
    (params) => {
      return <TextField {...params} label={label} />;
    },
    [label]
  );

  return (
    <FormControl sx={{ marginTop: '12px' }}>
      <StyledAutocomplete size="small" renderInput={renderInput} options={[]} disabled />
    </FormControl>
  );
};

const StyledAutocomplete = styled(Autocomplete)`
  min-width: 290px;
`;
